/**
 * `/agents/workflows/$slug` — the Workflow Studio. RO-3-02b.
 *
 * ── Why this page was ported ahead of the remaining L3 sections ──
 *
 * PRD §11.3 chose to port `/agents` FIRST, against the instinct to do it last,
 * for exactly one reason: `docs/agents/RECURSIVE-WORKFLOWS-COMPLETION-PRD.md` is
 * a CONCURRENT program building new panels onto THIS page, and porting first
 * means it builds them once, in this stack, instead of in `excellent/app/` and
 * then again here. RO-3-02 (#44295) landed 10 of the 37 `/agents` pages and
 * deferred this one — the biggest single deferral, and the one the no-double-work
 * guarantee was written for. This closes that.
 *
 * It is a PORT. The design is the audited one (`ce70c9bd`, `5aaed10b`,
 * `20036b14` and `docs/.../WORKFLOW-DETAIL-PARITY-AUDIT.md`); every finding those
 * commits settled is preserved, including the ones whose whole point is a
 * NEGATIVE — no `Active version` chip and no `Status` badge in the header (§3:
 * the badge duplicated the On/Off switch two inches away and reused the `Radio`
 * glyph that means "Live"), the pipeline bled on the RIGHT only (§5 F19), and one
 * source for every number in the Completed-runs tile (§4 F12).
 *
 * ── The three structural changes a Vite client forces ──
 *
 * 1. The server render becomes ONE contracted read. `LoopStudioPage` composed
 *    eleven `@/optimization/*` loaders; `workflowDetail.get` is that composition,
 *    server-side, in `excellent/server/routes/agent-workflows.ts`.
 * 2. Five server-action FUNCTION REFERENCES were passed down as props
 *    (`runAction={runLoopNowAction}` and four imported straight into islands). A
 *    function cannot cross the wire, so each is now a mutation on the same
 *    contract, called with `apiCall` at the same place in the tree.
 * 3. `router.refresh()` was how every write re-read the server render. Here that
 *    is `mutate(apiKey(workflowDetail.get, …))` — SWR stays the data layer (PRD
 *    D3); only the transport under it changed.
 *
 * ── Typed search, not raw strings ──
 *
 * The original read `tab`, `range`, `failed`, `role`, `from`, `to` and `sub` off
 * `useSearchParams()`. All seven are declared below through `routeSearchSchema`,
 * which refuses to compile a key the shell already owns. That matters here
 * specifically: `?task=` is the object peek's and `?peek=` is shell-wide, so a
 * page inventing its own would silently drive the peek host instead. `sub=trace`
 * is declared too, because the original's `redirect()` on it is now a
 * `beforeLoad` redirect and an undeclared param would be dropped before the
 * cascade could read it.
 */

import * as React from "react";
import { z } from "zod";
import { createRoute, redirect } from "@tanstack/react-router";
import { mutate } from "swr";
import {
  Button,
  Card,
  CardStrip,
  Badge,
  Chip,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  DropdownMenu,
  Hint,
  IconTooltip,
  IntentBadge,
  MiniStatStrip,
  PageHeader,
  RunsActivityCard,
  SectionCard,
  SectionNav,
  Sparkline,
  StatStrip,
  Switch,
  WorkflowFailedRunsCard,
  WorkflowRunsTable,
  cn,
  formatNumber,
  formatUsd,
  modelLabel,
  type MetaFact,
  type StatStripItem,
} from "@excellent/ui";
import {
  ArrowRight,
  Bot,
  Boxes,
  Check,
  ChevronDown,
  Cpu,
  Ellipsis,
  Gauge,
  Radio,
  ShieldCheck,
  TriangleAlert,
  UserRound,
  Workflow,
  X,
} from "@excellent/ui/icons";
import { useApi, apiCall, apiKey } from "@excellent/ui/api";
import { RouterLink as Link } from "@excellent/ui/router";
import {
  workflowDetail,
  type WorkflowDetailPayload,
  type WorkflowOverviewDto,
  type WorkflowStepDto,
} from "@excellent/ui/api/contracts/workflows/detail";
import {
  DETAIL_RANGES,
  parseDetailRange,
  parseDetailTab,
  type DetailRangeKey,
  type DetailTabKey,
} from "@/optimization/workflow-detail-view";
import { rootRoute } from "./__root";
import { routeSearchSchema } from "../shell/search";
import { TriggerBuilder } from "./workflows/studio-trigger";
import { OptimizeTab } from "./workflows/studio-optimize";
import { DoneTab } from "./workflows/studio-done";
import { workflowDone } from "@excellent/ui/api/contracts/workflows/done";

// ── formatting (verbatim from `_studio.tsx`) ──────────────────────────────────

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
/** `ship-verify` → `Ship → Verify`. A gate policy is a HANDOFF, and the meta
 *  strip reads it aloud as one — the raw slug is an address, not a label. */
function formatGatePolicy(policy: string | null | undefined): string {
  if (!policy) return "—";
  return policy.split("-").map(cap).join(" → ");
}
function fmtDur(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return "—";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return s % 60 ? `${m}m ${s % 60}s` : `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}
function fmtTokens(n: number | null | undefined): string {
  if (n == null) return "—";
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(Math.round(n));
}
function pct0(x: number): string {
  return `${Math.round(x * 100)}%`;
}
function fmtRoi(roi: number | null): string {
  return roi == null ? "—" : `${roi >= 10 ? Math.round(roi) : roi.toFixed(1)}×`;
}
function rangeSuffix(range: DetailRangeKey): string {
  return range === "all" ? "all time" : range;
}

const TABS: { key: DetailTabKey; label: string }[] = [
  { key: "overview", label: "Overview" },
  { key: "pipeline", label: "Pipeline" },
  { key: "runs", label: "Runs" },
  { key: "optimize", label: "Optimization" },
  { key: "done", label: "Done" },
];

// ── the route ─────────────────────────────────────────────────────────────────

const studioSearchSchema = routeSearchSchema({
  /** Which tab. Three readers cast `tab` to three DIFFERENT local unions across
   *  the app, which is why it is route-local and NOT shell-wide. */
  tab: z.string().optional(),
  /** Report window (`1d|7d|30d|all`) — a different vocabulary from the fleet's. */
  range: z.string().optional(),
  /** Step name a failure strip drilled in on; opens the Runs tab filtered. */
  failed: z.string().optional(),
  /** Role scope that travels with `failed` (the ledger attributes runs by role). */
  role: z.string().optional(),
  /**
   * Inclusive day bounds on the runs table / activity chart (ISO `YYYY-MM-DD`).
   *
   * They are `dayFrom`/`dayTo`, NOT `from`/`to` (#44400). `from` is shell-wide
   * (`NAV_FROM_PARAM`, read by the mode rail and the breadcrumb as BACK-LINK
   * PROVENANCE — see `shell/search.ts`), and this page means a DATE, so the two
   * readers disagreed about what the value was: the runs table wrote
   * `?from=2026-07-29` and the chrome read that date as the place you came from.
   * `routeSearchSchema` caught it as a compile error rather than a grep
   * (`ERROR_param_is_shell_wide_and_already_claimed`), which is what a shell-wide
   * namespace declared in the type system buys you.
   *
   * The rename went to the NARROWER consumer — this page's day filter — because
   * the shell-wide key is read on every route. These are route-local names now,
   * so they declare cleanly here, and the shared block
   * (`packages/ui/src/blocks/optimization/workflow-runs-table.tsx`) reads the
   * same two keys. The `workflowRunsQuery` CONTRACT fields stay `from`/`to`:
   * those are server-side and unambiguous, never URL param names.
   */
  dayFrom: z.string().optional(),
  dayTo: z.string().optional(),
  /**
   * Done-tab task id (`?done=<id>`). NOT `?task=` — `task` is shell-wide object
   * peek (W1-A2 / #44605, same hazard the prototype documents). Digits only.
   */
  done: z.string().regex(/^\d+$/).optional(),
  // `sub` is likewise shell-wide (preserved deep sub-route) and likewise refused.
  // The original's `?sub=trace` deep-link therefore reads it from the root's
  // inherited search in `beforeLoad` below — the chrome already owns and preserves
  // the key, so this needs no route-local declaration at all.
});

export const agentsWorkflowRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/workflows/$slug",
  validateSearch: studioSearchSchema,
  /**
   * The original's first statement was `if (sp.sub === "trace") redirect(...)`.
   * Next threw `redirect()` out of an async server component; TanStack throws a
   * typed `redirect` out of `beforeLoad`. Same first-match-wins control flow, one
   * phase earlier.
   */
  beforeLoad: ({ params, search }) => {
    if (search.sub === "trace") {
      throw redirect({ to: `/agents/workflows/${params.slug}/trace`, replace: true });
    }
  },
  component: WorkflowStudioRoute,
});

function WorkflowStudioRoute() {
  const { slug } = agentsWorkflowRoute.useParams();
  const search = agentsWorkflowRoute.useSearch();
  const navigate = agentsWorkflowRoute.useNavigate();

  const range = parseDetailRange(search.range ?? null);
  const tab = parseDetailTab(search.tab ?? null);
  // Opening runs with a failure drill-in when `failed` is present.
  const activeTab: DetailTabKey = search.failed ? "runs" : tab;

  const args = { params: { slug }, query: { range } } as const;
  const { data, error, isLoading } = useApi(workflowDetail.get, args);

  /** `router.refresh()`'s honest equivalent: re-read this route's own key. */
  const refresh = React.useCallback(() => {
    void mutate(apiKey(workflowDetail.get, args));
  }, [slug, range]); // eslint-disable-line react-hooks/exhaustive-deps

  const setQuery = React.useCallback(
    (patch: Record<string, string | null>) => {
      void navigate({
        search: (prev) => {
          const next: Record<string, unknown> = { ...prev };
          for (const [k, v] of Object.entries(patch)) {
            if (v == null || v === "") delete next[k];
            else next[k] = v;
          }
          return next as never;
        },
        replace: true,
      });
    },
    [navigate],
  );

  if (error) {
    return (
      <div className="px-6 py-8">
        <p className="type-body text-destructive">{error.message ?? String(error)}</p>
      </div>
    );
  }
  if (isLoading || !data) {
    return <div className="px-6 py-8 type-body text-muted-foreground">Loading…</div>;
  }
  if (!data.detail) {
    // The original's `notFound()`. Rendered rather than thrown, so the shell's
    // chrome and the back-link survive — a bad slug is a wrong turn, not a crash.
    return (
      <div className="flex flex-col gap-3 px-6 py-8">
        <h1 className="type-heading font-medium text-foreground">No such workflow</h1>
        <p className="type-body text-muted-foreground">
          Nothing in the vault is registered under <span className="font-mono">{slug}</span>.
        </p>
        <Button asChild variant="outline" className="self-start">
          <Link href="/agents/workflows">← Workflows</Link>
        </Button>
      </div>
    );
  }

  return (
    <WorkflowStudio
      detail={data.detail}
      activeTab={activeTab}
      activeRange={range}
      failedStepName={search.failed ?? null}
      selectedFrom={search.dayFrom ?? null}
      selectedTo={search.dayTo ?? null}
      doneTaskId={search.done ?? null}
      setQuery={setQuery}
      refresh={refresh}
    />
  );
}

// ══ the studio shell (port of `_studio.tsx`'s `WorkflowStudio`) ═══════════════

function WorkflowStudio({
  detail,
  activeTab,
  activeRange,
  failedStepName,
  selectedFrom,
  selectedTo,
  doneTaskId,
  setQuery,
  refresh,
}: {
  detail: WorkflowDetailPayload;
  activeTab: DetailTabKey;
  activeRange: DetailRangeKey;
  failedStepName: string | null;
  selectedFrom: string | null;
  selectedTo: string | null;
  /** `?done=<id>` for the Done tab — never `?task=` (peek collision). */
  doneTaskId: string | null;
  setQuery: (patch: Record<string, string | null>) => void;
  refresh: () => void;
}) {
  const {
    slug,
    loop,
    activeDays,
    steps,
    totalFailed,
    overview,
    trigger,
    missingRoles,
    initialEnvHint,
    ladderSteps,
    availableRoles,
    stepWorkflowSlugs,
    optimize,
  } = detail;

  const [on, setOn] = React.useState(loop.status === "active");
  const [pending, setPending] = React.useState(false);
  React.useEffect(() => {
    setOn(loop.status === "active");
  }, [loop.status]);

  // Done tab — separate contract so the detail payload stays the studio composition
  // and a server-only loader never enters the client graph (W1-A2 / #44605).
  // `args: null` pauses the fetch until the tab is selected.
  const doneArgs =
    activeTab === "done"
      ? {
          params: { slug },
          query: doneTaskId ? { done: Number(doneTaskId) } : ({} as { done?: number }),
        }
      : null;
  const {
    data: doneData,
    error: doneError,
    isLoading: doneLoading,
  } = useApi(workflowDone.get, doneArgs);

  function toggleOn(next: boolean) {
    setOn(next);
    setPending(true);
    void (async () => {
      try {
        const res = await apiCall(workflowDetail.setStatus, {
          params: { slug },
          body: { status: next ? "active" : "paused" },
        });
        if (!res.ok) setOn(loop.status === "active");
        else refresh();
      } catch {
        setOn(loop.status === "active");
      } finally {
        setPending(false);
      }
    })();
  }

  const failedStep = failedStepName
    ? steps.find((s) => s.name === failedStepName || s.role === failedStepName) ?? null
    : null;

  // FOUR facts, matching the audited design (§3): no `Active version` chip (the
  // version story lives on the Optimization tab) and no `Status` badge (it
  // duplicated the On/Off switch two inches away and reused the `Radio` glyph
  // that means "Live" — one glyph, one meaning, per page). Gate renders formatted
  // ("Ship → Verify"), never the raw slug.
  const meta: MetaFact[] = [
    { icon: <Boxes className="size-3.5" />, label: "Domain", value: cap(loop.domain ?? "—") },
    {
      icon: <ShieldCheck className="size-3.5" />,
      label: "Gate",
      value: formatGatePolicy(loop.gate_policy),
    },
    {
      icon: <UserRound className="size-3.5" />,
      label: "Runs as",
      value: loop.owner_agent_id ? cap(loop.owner_agent_id) : "—",
    },
    {
      icon: <Radio className="size-3.5" />,
      label: "Live",
      value: activeDays > 0 ? `${activeDays} days` : "no runs yet",
    },
  ];

  return (
    <div className="@container/page px-4 pt-5 pb-8">
      <div className="mx-auto flex max-w-[1180px] flex-col gap-6 px-6">
        <PageHeader
          ownTitle
          back={{ label: "Workflows", href: "/agents/workflows" }}
          title={loop.title}
          description={loop.description || undefined}
          meta={meta}
          // The header carries IDENTITY controls only (on/off + the ⋯ menu):
          // running a pass lives in the Pipeline tab's run console and in the ⋯
          // menu — not as a primary button with a raw env input on the title row
          // (audit §3 F4/F5).
          toolbar={
            <>
              <OnOffSwitch
                on={on}
                setOn={toggleOn}
                disabled={pending || loop.status === "retired"}
              />
              <HeaderMenu slug={slug} />
            </>
          }
          tabs={TABS.map((t) => ({ href: t.key, label: t.label, activeHref: t.key }))}
          navValue={activeTab}
          onNavSelect={(key) => {
            const k = key as DetailTabKey;
            setQuery({ tab: k, ...(k !== "runs" ? { failed: null, role: null } : {}) });
          }}
          navRight={
            <RangeSelect value={activeRange} onChange={(r) => setQuery({ range: r })} />
          }
        />

        {activeTab === "overview" && (
          <OverviewTab
            overview={overview}
            steps={steps}
            stepWorkflowSlugs={stepWorkflowSlugs}
            onOpenTab={(t) => setQuery({ tab: t, failed: null })}
          />
        )}
        {activeTab === "pipeline" && (
          <PipelineTab
            slug={slug}
            loopStatus={loop.status}
            gatePolicy={loop.gate_policy}
            steps={steps}
            ladderSteps={ladderSteps}
            availableRoles={availableRoles}
            stepWorkflowSlugs={stepWorkflowSlugs}
            totalFailed={totalFailed}
            trigger={trigger}
            missingRoles={missingRoles}
            initialEnvHint={initialEnvHint}
            refresh={refresh}
            onOpenRuns={(stepName, role) =>
              setQuery({ tab: "runs", failed: stepName, role: role ?? null })
            }
            onOpenOptimization={() => setQuery({ tab: "optimize", failed: null })}
          />
        )}
        {activeTab === "runs" && (
          <RunsTab
            loopId={loop.id}
            rangeDays={DETAIL_RANGES.find((r) => r.key === activeRange)?.days ?? null}
            failedStep={failedStep}
            selectedFrom={selectedFrom}
            selectedTo={selectedTo}
            onClearFilter={() => setQuery({ failed: null, role: null })}
            onSelectDay={(day) => {
              // A day click writes the SAME ?dayFrom/?dayTo the table filters on,
              // so the chart and the table can never disagree; clicking the marked
              // day again clears it. NOT `from`/`to` — `from` is shell-wide
              // back-link provenance (`NAV_FROM_PARAM`), read by the mode rail and
              // breadcrumb on every route (#44400).
              const cleared = selectedFrom === day && selectedTo === day;
              setQuery(cleared ? { dayFrom: null, dayTo: null } : { dayFrom: day, dayTo: day });
            }}
          />
        )}
        {activeTab === "optimize" && (
          <OptimizeTab
            slug={slug}
            optimize={optimize}
            ladderSteps={ladderSteps}
            onChanged={refresh}
          />
        )}
        {activeTab === "done" && (
          <div className="px-6 py-4">
            {doneError ? (
              <p className="type-body text-destructive">{doneError.message ?? String(doneError)}</p>
            ) : doneLoading || !doneData ? (
              <p className="type-body text-muted-foreground">Loading Done…</p>
            ) : (
              <DoneTab
                slug={slug}
                task={doneData.task}
                readable={doneData.readable}
                unreadableReason={doneData.unreadableReason}
                candidates={doneData.candidates}
                deltaReceipts={doneData.deltaReceipts}
                stampTotals={doneData.stampTotals}
                isFallback={doneData.isFallback}
                onSelectTask={(id) => setQuery({ tab: "done", done: String(id) })}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── header bits ──────────────────────────────────────────────────────────────

function OnOffSwitch({
  on,
  setOn,
  disabled,
}: {
  on: boolean;
  setOn: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label
      className={cn(
        "flex h-7 cursor-pointer items-center gap-2 rounded-full border border-border px-2.5 transition-colors",
        disabled && "pointer-events-none opacity-50",
      )}
    >
      <Switch
        checked={on}
        onCheckedChange={setOn}
        size="sm"
        disabled={disabled}
        className={cn(
          "data-[state=checked]:bg-success data-[state=unchecked]:bg-destructive",
          "[&_[data-slot=switch-thumb]]:bg-white",
        )}
      />
      <span className="type-body font-medium text-foreground">{on ? "On" : "Off"}</span>
    </label>
  );
}

/**
 * The ⋯ menu — the audited item set (§3 F6), each item backed by a REAL action.
 * "Trigger trace" left the menu (the trigger card's "View trace" is the one
 * doorway); "Edit with chat" and the destructive "Delete workflow" are deferred
 * until their seams exist (no dock-chat prefill; no retire/delete endpoint) —
 * deferrals are tracked on the parity program, not faked here.
 */
function HeaderMenu({ slug }: { slug: string }) {
  const [copied, setCopied] = React.useState(false);
  const [runState, setRunState] = React.useState<"idle" | "pending" | "ok" | "err">("idle");
  const [optState, setOptState] = React.useState<"idle" | "pending" | "ok" | "err">("idle");
  const cli = `excellent-mcp agent run-loop ${slug}`;

  // Menu items close the menu on select, so each async item reports back through
  // its own label (the Copy-CLI pattern): reopen the menu and the outcome is
  // there. The transient state resets after a few seconds.
  function settle(set: (s: "idle" | "pending" | "ok" | "err") => void, s: "ok" | "err") {
    set(s);
    window.setTimeout(() => set("idle"), 4000);
  }

  const runLabel = {
    idle: "Run a pass",
    pending: "Starting a pass…",
    ok: "Pass started — see Runs",
    err: "Couldn't start — see Pipeline",
  }[runState];
  const optLabel = {
    idle: "Optimize now",
    pending: "Proposing…",
    ok: "Proposal drafted — see Optimization",
    err: "Couldn't propose a variant",
  }[optState];

  return (
    <DropdownMenu
      items={[
        {
          label: runLabel,
          onSelect: () => {
            if (runState === "pending") return;
            setRunState("pending");
            void apiCall(workflowDetail.run, { params: { slug }, body: {} }).then(
              (res) => settle(setRunState, res.ok ? "ok" : "err"),
              () => settle(setRunState, "err"),
            );
          },
        },
        {
          label: optLabel,
          onSelect: () => {
            if (optState === "pending") return;
            setOptState("pending");
            void apiCall(workflowDetail.optimizeNow, { params: { slug }, body: {} }).then(
              (res) => settle(setOptState, res && res.ok === false ? "err" : "ok"),
              () => settle(setOptState, "err"),
            );
          },
        },
        { label: "Arm a trigger…", href: `/agents/workflows/${slug}?tab=pipeline` },
        { type: "separator" },
        {
          label: copied ? "Copied CLI command" : "Copy CLI command",
          onSelect: () => {
            void navigator.clipboard?.writeText(cli);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1200);
          },
        },
      ]}
    >
      <Button variant="outline" size="icon-tb" aria-label="More actions">
        <Ellipsis />
      </Button>
    </DropdownMenu>
  );
}

function RangeSelect({
  value,
  onChange,
}: {
  value: DetailRangeKey;
  onChange: (r: DetailRangeKey) => void;
}) {
  const cur = DETAIL_RANGES.find((r) => r.key === value) ?? DETAIL_RANGES[2]!;
  return (
    <DropdownMenu
      align="end"
      widthPx={140}
      items={DETAIL_RANGES.map((r) => ({
        label: r.label,
        selected: r.key === value,
        onSelect: () => onChange(r.key),
      }))}
    >
      <button
        type="button"
        aria-label="Report range"
        className="flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 type-body text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
      >
        {cur.label}
        <ChevronDown className="size-3.5" />
      </button>
    </DropdownMenu>
  );
}

// ══ OVERVIEW ═════════════════════════════════════════════════════════════════

function OverviewTab({
  overview,
  steps,
  stepWorkflowSlugs,
  onOpenTab,
}: {
  overview: WorkflowOverviewDto;
  steps: WorkflowStepDto[];
  stepWorkflowSlugs: string[];
  onOpenTab: (t: DetailTabKey) => void;
}) {
  const v = overview.valueView;
  const p = v.projected;
  const win = rangeSuffix(overview.range);
  const h = overview.runHealth;

  const hero: StatStripItem[] = [
    {
      title: `Net value · ${win}`,
      value: formatUsd(p.net_value_usd),
      footer: [{ count: fmtRoi(p.roi), label: "ROI on agent spend", intent: "success" }],
      accentColor: "var(--success)",
    },
    {
      title: `Time saved · ${win}`,
      value: `${formatNumber(p.time_saved_hours, { maxFractionDigits: 0 })} hrs`,
      description: `≈ ${formatUsd(p.time_saved_usd)} of human work`,
    },
    {
      // ONE source for the whole sentence: runHealth's completed/total/passRate.
      // Mixing the value model's terminal-run count with runHealth's
      // all-statuses rate printed "1,104 of 1,935 · 54% pass" — a percentage its
      // own numbers compute to 57% (audit §4 F12). Every number in this tile now
      // reconciles with the Health card below it.
      title: "Completed runs",
      value: formatNumber(h.completed),
      description: `of ${formatNumber(h.total)} · ${pct0(h.passRate)} pass`,
    },
    {
      title: `Agent spend · ${win}`,
      value: formatUsd(v.measured.agent_cost_usd),
      description: `${formatNumber(v.annual.runs_per_day, { maxFractionDigits: 1 })} runs/day`,
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="-mx-6">
        <StatStrip items={hero} variant="standalone" />
      </div>
      <div className="-mx-6 grid gap-6 @2xl/page:grid-cols-2">
        <ValueCard view={v} />
        <HealthCard health={h} daily={overview.daily} onOpenRuns={() => onOpenTab("runs")} />
      </div>
      <SectionCard
        title="Pipeline"
        action={<SectionNav label="Open pipeline" onClick={() => onOpenTab("pipeline")} />}
      >
        <MiniChain
          steps={steps}
          workflowSlugs={stepWorkflowSlugs}
          onOpenPipeline={() => onOpenTab("pipeline")}
        />
        <Hint frame="px6py4">
          Every step is a doorway — open one to tune its role, model, or rung. Gated steps (the
          shield) hold the rule this workflow runs on: a different agent must verify.
        </Hint>
      </SectionCard>
    </div>
  );
}

function ValueCard({ view }: { view: WorkflowOverviewDto["valueView"] }) {
  const gross = view.projected.gross_value_usd;
  const spend = view.measured.agent_cost_usd;
  const net = view.projected.net_value_usd;
  const parts = [
    { label: "Time saved", usd: view.projected.time_saved_usd, color: "var(--success)" },
    { label: "Issues avoided", usd: view.projected.issues_avoided_usd, color: "var(--warning)" },
    { label: "Optimizer savings", usd: view.measured.realized_savings_usd, color: "var(--info)" },
  ].filter((x) => x.usd > 0);

  return (
    <SectionCard
      title="Value"
      aside={<IntentBadge intent="success">{fmtRoi(view.projected.roi)} ROI</IntentBadge>}
      bleed={false}
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-baseline justify-between type-subtext">
          <span className="text-muted-foreground">Gross value created</span>
          <span className="tabular-nums text-foreground">{formatUsd(gross)}</span>
        </div>
        {gross > 0 && parts.length > 0 && (
          <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-muted">
            {parts.map((x) => (
              <span
                key={x.label}
                className="h-full"
                style={{ width: `${(x.usd / gross) * 100}%`, background: x.color }}
              />
            ))}
          </div>
        )}
      </div>
      {/* Spend is the SECOND bar of the gross-vs-spend comparison, not a loose
          text row — the two bars are the card's whole argument (audit §4 F1). */}
      <div className="flex flex-col gap-2">
        <div className="flex items-baseline justify-between type-subtext">
          <span className="text-muted-foreground">− Agent spend</span>
          <span className="tabular-nums text-foreground">{formatUsd(spend)}</span>
        </div>
        {gross > 0 && (
          <div className="h-2.5 w-full overflow-hidden rounded-full bg-muted">
            <span
              className="block h-full rounded-full bg-muted-foreground/60"
              style={{ width: `${Math.min(100, (spend / gross) * 100)}%` }}
            />
          </div>
        )}
      </div>
      <div className="flex items-baseline justify-between border-t border-border/60 pt-3">
        <span className="type-body text-muted-foreground">Net value</span>
        <span className="type-title font-medium tabular-nums text-foreground">{formatUsd(net)}</span>
      </div>
      <Hint frame="px6py4">
        {formatNumber(view.measured.completed_runs)} completed runs ×{" "}
        {view.assumptions.minutes_per_run} min of human work at{" "}
        {formatUsd(view.assumptions.labor_rate_usd_per_hour)}/hr. Every figure is a measured count ×
        an assumption you can change.
      </Hint>
    </SectionCard>
  );
}

function RunningPulse({ n }: { n: number }) {
  if (n <= 0) return null;
  return (
    <span className="inline-flex items-center gap-1.5 type-subtext text-muted-foreground">
      <span className="relative flex size-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-60" />
        <span className="relative inline-flex size-2 rounded-full bg-success" />
      </span>
      {n} running now
    </span>
  );
}

function HealthCard({
  health,
  daily,
  onOpenRuns,
}: {
  health: WorkflowOverviewDto["runHealth"];
  daily: WorkflowOverviewDto["daily"];
  onOpenRuns: () => void;
}) {
  const trend = daily.map((d) => d.runs);
  return (
    <SectionCard
      title="Health"
      aside={<RunningPulse n={health.runningNow} />}
      action={<SectionNav label="All runs" onClick={onOpenRuns} />}
      bleed={false}
    >
      <div className="flex items-end gap-3">
        <span className="type-heading font-medium tabular-nums text-foreground">
          {pct0(health.passRate)}
        </span>
        <span className="pb-1 type-subtext text-muted-foreground">
          pass rate · {formatNumber(health.completed)} / {formatNumber(health.total)}
        </span>
      </div>
      {trend.length > 0 && (
        <Sparkline values={trend} color="var(--chart-2)" className="h-14 w-full" ariaHidden />
      )}
      {health.total === 0 && (
        <p className="type-body text-muted-foreground">No runs in this window yet.</p>
      )}
      {/* The per-role breakdown is its own defined block, not loose text after a
          rule — the same strip treatment as every card footer on the page. */}
      {health.byRole.length > 0 && (
        <MiniStatStrip
          frame="px6py4"
          stats={health.byRole.map((r) => ({
            label: cap(r.role),
            value: `${formatNumber(r.runs)} runs · ${formatUsd(r.avg_cost)}/run`,
            sub: `${fmtDur(r.avg_wall_ms)} avg · ${fmtTokens(r.avg_tokens)} tok`,
          }))}
        />
      )}
    </SectionCard>
  );
}

/** The chain is a set of doorways, not an inert diagram (audit §4 F11) — but a
 *  doorway has to lead somewhere that EXISTS. A step is usually a role, not a
 *  loop, so `/agents/workflows/<step>` 404s for all but the rare step that is
 *  itself a workflow; those get the Workflow glyph and the direct link, and
 *  everything else opens the pipeline, where the step's own card lives. */
function MiniChain({
  steps,
  workflowSlugs,
  onOpenPipeline,
}: {
  steps: WorkflowStepDto[];
  workflowSlugs: string[];
  onOpenPipeline: () => void;
}) {
  if (steps.length === 0) {
    return <p className="type-body text-muted-foreground">No steps defined.</p>;
  }
  const nodeClass =
    "group inline-flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-left transition-colors hover:border-primary/60 hover:bg-muted/40";
  return (
    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2">
      <MiniNode icon={<Radio className="size-4" />} label="Trigger" />
      {steps.map((s) => {
        const isWorkflow = workflowSlugs.includes(s.name);
        const body = (
          <>
            <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-foreground type-subtext font-medium tabular-nums text-background">
              {s.ordinal}
            </span>
            <span className="type-body font-medium text-foreground">{cap(s.name)}</span>
            <ShieldCheck className="size-3.5 text-primary" aria-label="gated" />
            {isWorkflow && (
              <Workflow
                className="size-3.5 text-muted-foreground transition-colors group-hover:text-primary"
                aria-label="workflow"
              />
            )}
          </>
        );
        return (
          <React.Fragment key={s.id}>
            <ArrowRight className="size-4 shrink-0 text-muted-foreground/50" />
            {isWorkflow ? (
              <Link href={`/agents/workflows/${s.name}`} className={nodeClass}>
                {body}
              </Link>
            ) : (
              <button
                type="button"
                onClick={onOpenPipeline}
                aria-label={`Open ${cap(s.name)} in the pipeline`}
                className={nodeClass}
              >
                {body}
              </button>
            )}
          </React.Fragment>
        );
      })}
      <ArrowRight className="size-4 shrink-0 text-muted-foreground/50" />
      <MiniNode icon={<Check className="size-4 text-success" />} label="Done" />
    </div>
  );
}

function MiniNode({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <Badge
      variant="outline"
      className="gap-2 bg-muted/40 px-3 text-muted-foreground [&>svg]:size-4"
    >
      {icon}
      {label}
    </Badge>
  );
}

// ══ PIPELINE ═════════════════════════════════════════════════════════════════

function PipelineTab({
  slug,
  loopStatus,
  gatePolicy,
  steps,
  ladderSteps,
  availableRoles,
  stepWorkflowSlugs,
  totalFailed,
  trigger,
  missingRoles,
  initialEnvHint,
  refresh,
  onOpenRuns,
  onOpenOptimization,
}: {
  slug: string;
  loopStatus: string;
  gatePolicy: string;
  steps: WorkflowStepDto[];
  ladderSteps: WorkflowDetailPayload["ladderSteps"];
  availableRoles: string[];
  stepWorkflowSlugs: string[];
  totalFailed: number;
  trigger: WorkflowDetailPayload["trigger"];
  missingRoles: string[];
  initialEnvHint: string;
  refresh: () => void;
  onOpenRuns: (stepName: string, role: string | null) => void;
  onOpenOptimization: () => void;
}) {
  const totalCost = steps.reduce((s, x) => s + x.avg_cost, 0);
  const totalWall = steps.reduce((s, x) => s + x.avg_wall_ms, 0);
  const totalRuns = steps.reduce((s, x) => s + x.runs, 0);
  const automatic = Boolean(trigger.subscription?.enabled);
  const gated = gatePolicy === "ship-verify";
  const last = steps.length - 1;
  const descriptions = new Map(ladderSteps.map((l) => [l.id, l.description]));

  const stats: StatStripItem[] = [
    {
      title: "Didn't finish",
      value: formatNumber(totalFailed),
      description:
        totalRuns > 0
          ? `${pct0(totalFailed / totalRuns)} of ${formatNumber(totalRuns)} runs`
          : "no step runs yet",
    },
    {
      title: "Cost / pass",
      value: formatUsd(totalCost),
      description: steps.length > 0 ? `${formatUsd(totalCost / steps.length)} avg / step` : "—",
    },
    {
      title: "Time / pass",
      value: fmtDur(totalWall),
      description: steps.length > 0 ? `${fmtDur(totalWall / steps.length)} avg / step` : "—",
    },
    {
      title: "Trigger",
      value: automatic ? "Automatic" : "Manual",
      description: automatic
        ? `fires on ${trigger.subscription?.event_type_prefix ?? "events"}`
        : "no automatic firings — run it to start",
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="-mx-6">
        <StatStrip items={stats} variant="standalone" />
      </div>

      {/* Bled on the RIGHT only. The rail's nodes are the left edge of this
          surface — they already sit on the text rail — but the card column is a
          card frame like any other and must end on the same outer edge as the
          stat strip above: `-mr-6`. Without it the whole pipeline reads 24px
          narrower than everything around it (audit §5 F19). */}
      <div className="-mr-6 flex flex-col">
        <RailRow node={<IconNode icon={Radio} />}>
          {/* TriggerBuilder renders its own Card — wrapping it in a second one
              double-framed and double-titled the stage (audit §5 F10). */}
          <TriggerBuilder
            slug={slug}
            loopStatus={loopStatus}
            trigger={trigger}
            missingRoles={missingRoles}
            initialEnvHint={initialEnvHint}
          />
        </RailRow>

        {steps.map((s, i) => (
          <RailRow key={s.id} node={<StepNode n={s.ordinal} />}>
            {i === last && gated ? (
              <div className="flex flex-col gap-2.5">
                <StepCard
                  slug={slug}
                  step={s}
                  description={descriptions.get(s.id) ?? null}
                  gated={gated}
                  availableRoles={availableRoles}
                  workflowHref={
                    stepWorkflowSlugs.includes(s.name) ? `/agents/workflows/${s.name}` : null
                  }
                  refresh={refresh}
                  onOpenRuns={onOpenRuns}
                  onOpenOptimization={onOpenOptimization}
                />
                <GateReasonNote />
              </div>
            ) : (
              <StepCard
                slug={slug}
                step={s}
                description={descriptions.get(s.id) ?? null}
                gated={gated}
                availableRoles={availableRoles}
                workflowHref={
                  stepWorkflowSlugs.includes(s.name) ? `/agents/workflows/${s.name}` : null
                }
                refresh={refresh}
                onOpenRuns={onOpenRuns}
                onOpenOptimization={onOpenOptimization}
              />
            )}
          </RailRow>
        ))}

        <RailRow node={<IconNode icon={Check} tone="success" />} last>
          <DoneCard />
        </RailRow>
      </div>

      <Hint frame="loose">
        The run flows top to bottom, traced down the left. On each step, role, model and rung are
        live — click one to change it. Gated isn&rsquo;t: that is the workflow&rsquo;s rule, shared by
        both steps. Every way a step&rsquo;s runs end badly is counted where it happens, and opens the
        runs behind it.
      </Hint>
    </div>
  );
}

function RailRow({
  node,
  last,
  children,
}: {
  node: React.ReactNode;
  last?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center gap-1.5">
        {node}
        {!last && <span className="w-px flex-1 bg-border" />}
      </div>
      <div className={cn("min-w-0 flex-1", !last && "pb-5")}>{children}</div>
    </div>
  );
}

function StepNode({ n }: { n: number }) {
  return (
    <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-foreground type-body font-medium tabular-nums text-background">
      {n}
    </span>
  );
}

function IconNode({
  icon: Icon,
  tone,
}: {
  icon: React.ComponentType<{ className?: string }>;
  tone?: "success";
}) {
  return (
    <span className="flex size-8 shrink-0 items-center justify-center rounded-full border border-border bg-muted">
      <Icon
        className={cn("size-4", tone === "success" ? "text-success" : "text-muted-foreground")}
      />
    </span>
  );
}

// ── a step's meta tags ───────────────────────────────────────────────────────
//
// A step reads in the same three bands the page header gives the workflow itself
// — TITLE → DESCRIPTION → TAGS — because a step IS a small workflow. Two kinds
// of tag: a FACT is plain (Gated — the workflow's shared rule, not a per-step
// choice); a SETTING is a button with a chevron (role · model · rung — stored on
// the step, and changing one really changes what runs).

function StepTag({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: React.ReactNode;
  tone?: "primary";
}) {
  return (
    <span className="inline-flex items-center gap-1.5 type-body text-foreground">
      <IconTooltip label={label}>
        <span
          className={cn(
            "inline-flex cursor-default",
            tone === "primary" ? "text-primary" : "text-muted-foreground",
          )}
        >
          <Icon className="size-3.5" />
        </span>
      </IconTooltip>
      {value}
    </span>
  );
}

/** The editable twin: same icon + value, but the whole run is the trigger and a
 *  chevron says so. `label` and `detail` stay separate — the tag shows the
 *  readable value ("Haiku 4.5"), the tooltip carries the exact one underneath,
 *  and only `label` reaches `aria-label`. */
const STEP_TAG_TRIGGER =
  "-mx-1.5 inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 type-body text-foreground transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:outline-none";

/** The bare trigger both editable tags share, so a searchable picker and a plain
 *  menu are indistinguishable until they open. Forwards ref + props because
 *  Radix's `asChild` (DropdownMenu's trigger, IconTooltip's) clones it. */
const StepTagButton = React.forwardRef<
  HTMLButtonElement,
  // `value` is a native button attribute (a string); ours is the rendered tag
  // body, so it has to displace it rather than intersect with it.
  Omit<React.ComponentPropsWithoutRef<"button">, "value"> & {
    icon: React.ComponentType<{ className?: string }>;
    value: React.ReactNode;
  }
>(function StepTagButton({ icon: Icon, value, ...props }, ref) {
  return (
    <button ref={ref} type="button" className={STEP_TAG_TRIGGER} {...props}>
      <Icon className="size-3.5 text-muted-foreground" />
      {value}
      <ChevronDown className="size-3 text-muted-foreground" />
    </button>
  );
});

/** The hover copy every editable tag carries: what it is, that it is clickable,
 *  and the exact underlying value in mono. */
function stepTagLabel(label: string, detail?: string) {
  return (
    <span className="flex flex-col gap-0.5">
      <span>{label} — click to change</span>
      {detail && <span className="font-mono type-subtext opacity-70">{detail}</span>}
    </span>
  );
}

function StepSettingTag({
  icon,
  label,
  detail,
  value,
  items,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  detail?: string;
  value: React.ReactNode;
  items: { label: string; selected?: boolean; disabled?: boolean; onSelect?: () => void }[];
}) {
  // Tooltip OUTSIDE the menu: inverted, DropdownMenu's `asChild` clones the
  // tooltip wrapper instead of the button and the menu never opens.
  return (
    <IconTooltip label={stepTagLabel(label, detail)}>
      <DropdownMenu align="start" widthPx={260} items={items}>
        <StepTagButton icon={icon} value={value} aria-label={`${label}: change`} />
      </DropdownMenu>
    </IconTooltip>
  );
}

/** Role is the one setting whose catalog is unbounded — the fleet authors
 *  hundreds. A flat menu of 395 entries is a wall, not a choice, so role gets the
 *  searchable command picker the database surfaces already use, while model and
 *  rung (4 and 5 entries) stay plain menus. */
function StepRolePicker({
  role,
  roles,
  onPick,
}: {
  role: string | null;
  roles: string[];
  onPick: (next: string) => void;
}) {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <IconTooltip label={stepTagLabel("Role", role ?? "no role")}>
        <StepTagButton
          icon={Bot}
          value={role ? cap(role) : "No role"}
          aria-label="Role: change"
          onClick={() => setOpen(true)}
        />
      </IconTooltip>
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Search roles…" />
        <CommandList>
          <CommandEmpty>No role by that name.</CommandEmpty>
          <CommandGroup heading="Roles the runner can load">
            {roles.map((r) => (
              <CommandItem
                key={r}
                value={r}
                onSelect={() => {
                  setOpen(false);
                  if (r !== role) onPick(r);
                }}
              >
                <Bot className="size-3.5 text-muted-foreground" />
                {cap(r)}
                {r === role && <Check className="ml-auto size-3.5 text-primary" />}
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  );
}

/** The cheaper models a step can be PROPOSED down to, labelled by what the choice
 *  costs — the only reason anyone opens this menu. Picking one does not edit
 *  production: it proposes a routing candidate variant for the Optimization tab to
 *  evaluate and promote. */
const STEP_MODELS: { id: string; note: string }[] = [
  { id: "claude-haiku-4-5", note: "cheapest" },
  { id: "claude-sonnet-5", note: "the fleet's floor for shipping" },
  { id: "claude-opus-4-8", note: "most capable, most expensive" },
];

/** The automation ladder, read-only on the card. Every writer of a step's rung is
 *  the Optimization Harness — a promoted variant (rung ②), a promoted script
 *  (rung ④), a rollback. The card SHOWS the rung so the ladder stays legible and
 *  points at the control that moves it. */
const RUNG_NOTES: Record<number, string> = {
  1: "a full model run",
  2: "a smaller, cheaper model, escalating when unsure",
  3: "a memoized answer",
  4: "deterministic code",
  5: "no longer needed",
};

/** A step is its own section: title, what it does, then its tags. Two of them
 *  act: ROLE edits in place (orchestration config, no other writer), MODEL
 *  proposes a routing candidate through the optimizer. Rung and the gate are
 *  facts — the Harness owns them. Deliberately NOT one big link: half this card is
 *  controls, and a control inside an anchor is both invalid markup and
 *  un-clickable; "Open workflow" carries the link. */
function StepCard({
  slug,
  step,
  description,
  gated,
  availableRoles,
  workflowHref,
  refresh,
  onOpenRuns,
  onOpenOptimization,
}: {
  slug: string;
  step: WorkflowStepDto;
  description: string | null | undefined;
  gated: boolean;
  availableRoles: string[];
  /** Set only when this step is itself a workflow; null otherwise (no link). */
  workflowHref: string | null;
  refresh: () => void;
  onOpenRuns: (stepName: string, role: string | null) => void;
  onOpenOptimization: () => void;
}) {
  // Optimistic: the role tag flips immediately, reverts on a refused write. The
  // server is the source of truth — `refresh()` re-syncs after an ok.
  const [role, setRole] = React.useState(step.role);
  const [error, setError] = React.useState<string | null>(null);
  const [proposed, setProposed] = React.useState<string | null>(null);
  const [pending, setPending] = React.useState(false);
  React.useEffect(() => {
    setRole(step.role);
  }, [step.role]);

  function pickRole(next: string) {
    setError(null);
    setRole(next);
    void apiCall(workflowDetail.setStepRole, {
      params: { slug },
      body: { step: step.name, role: next },
    }).then(
      (res) => {
        if (!res.ok) {
          setRole(step.role);
          setError(res.error);
        } else {
          refresh();
        }
      },
      () => {
        setRole(step.role);
        setError("could not save");
      },
    );
  }

  /** A model pick is a PROPOSAL. `routeStep` writes a candidate variant;
   *  production keeps running the current model until that variant is promoted on
   *  the Optimization tab, so the tag deliberately does not move. */
  function proposeModel(modelId: string) {
    setError(null);
    setProposed(null);
    setPending(true);
    void apiCall(workflowDetail.routeStep, {
      params: { slug },
      body: { step: step.name, model: modelId },
    }).then(
      (res) => {
        setPending(false);
        if (res.ok) {
          setProposed(modelLabel(modelId));
          refresh();
        } else {
          setError(res.error ?? (res.data as { reason?: string } | undefined)?.reason ?? "unknown");
        }
      },
      () => {
        setPending(false);
        setError("could not propose");
      },
    );
  }

  const roles = availableRoles.length > 0 ? availableRoles : role ? [role] : [];
  const model = step.model;

  return (
    <Card className="flex flex-col gap-2 px-6 py-4">
      <div className="flex items-start justify-between gap-3">
        <h3 className="type-title font-medium text-foreground">{cap(step.name)}</h3>
        {workflowHref && (
          <Link
            href={workflowHref}
            className="group inline-flex shrink-0 items-center gap-1.5 type-subtext text-muted-foreground transition-colors hover:text-foreground"
          >
            <Workflow className="size-3.5" aria-hidden />
            Open workflow
            <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
          </Link>
        )}
      </div>
      {description && <p className="type-body text-muted-foreground">{description}</p>}
      <div className="mb-1 flex flex-wrap items-center gap-x-4 gap-y-1.5">
        {gated && (
          <StepTag
            icon={ShieldCheck}
            label="Gate — set by the workflow"
            value="Gated"
            tone="primary"
          />
        )}
        <StepRolePicker role={role} roles={roles} onPick={pickRole} />
        <StepSettingTag
          icon={Cpu}
          label="Model"
          detail={`${model ?? "role default"} — picking one PROPOSES a routing candidate`}
          value={model ? modelLabel(model) : "Role default"}
          items={STEP_MODELS.map((m) => ({
            label:
              m.id === model
                ? `${modelLabel(m.id)} — running now`
                : `Propose ${modelLabel(m.id)} — ${m.note}`,
            selected: m.id === model,
            disabled: m.id === model || pending,
            onSelect: m.id === model ? undefined : () => proposeModel(m.id),
          }))}
        />
        <StepTag
          icon={Gauge}
          label={`Rung — ${RUNG_NOTES[step.rung] ?? "the automation ladder"}. Moves through the optimizer, never a card click.`}
          value={`Rung ${step.rung}`}
        />
      </div>
      {error && (
        <p className="type-subtext text-destructive" aria-live="polite">
          Couldn&rsquo;t save: {error}
        </p>
      )}
      {proposed && (
        <p className="type-subtext text-muted-foreground" aria-live="polite">
          Proposed {proposed} as a candidate — evaluate and promote it on the{" "}
          <button
            type="button"
            onClick={() => onOpenOptimization()}
            className="text-foreground underline underline-offset-2 hover:text-primary"
          >
            Optimization tab
          </button>
          . Production still runs {model ? modelLabel(model) : "the role default"}.
        </p>
      )}
      {/* The card's foot: this step's measurements, then how its runs END — both
          the shared strip, BLED to the card edges so "Cost / run" sits on the
          same text rail as the title above it (audit §5 F6/F7). */}
      <div className="mt-auto flex flex-col">
        <MiniStatStrip
          frame="px6"
          stats={[
            { label: "Cost / run", value: formatUsd(step.avg_cost) },
            { label: "Tokens", value: fmtTokens(step.avg_tokens) },
            { label: "Avg time", value: fmtDur(step.avg_wall_ms) },
            { label: "Runs", value: formatNumber(step.runs) },
          ]}
        />
        <StepFailureStrip step={step} role={role} onOpenRuns={onOpenRuns} />
      </div>
    </Card>
  );
}

/** How often this step's runs DON'T finish — a count and a door, nothing more.
 *  The causes belong on the other side of the door, against the actual rows. */
function StepFailureStrip({
  step,
  role,
  onOpenRuns,
}: {
  step: WorkflowStepDto;
  role: string | null;
  onOpenRuns: (stepName: string, role: string | null) => void;
}) {
  const failed = step.failures.failed;

  if (failed === 0) {
    return (
      <CardStrip frame="px6py4" className="flex items-center gap-2 type-subtext text-muted-foreground">
        <Check className="size-3.5 shrink-0 text-success" />
        {step.runs > 0
          ? "Every run of this step finished. Nothing to look into."
          : "No runs attributed to this step yet."}
      </CardStrip>
    );
  }

  return (
    <CardStrip frame="px6py4" className="p-0">
      {/* The whole strip is the target, not a "See runs" link floating at the end
          of it: the number IS the thing you want to click. */}
      <button
        type="button"
        onClick={() => onOpenRuns(step.name, role)}
        className="group flex w-full flex-wrap items-center justify-between gap-x-3 gap-y-1 px-6 py-3 text-left transition-colors hover:bg-foreground/[0.03]"
      >
        <span className="inline-flex items-center gap-2 type-subtext">
          <TriangleAlert className="size-3.5 shrink-0 text-destructive" />
          <span className="text-foreground">{formatNumber(failed)} runs didn&rsquo;t finish</span>
          {step.runs > 0 && (
            <span className="text-muted-foreground">
              {pct0(failed / step.runs)} of {formatNumber(step.runs)}
            </span>
          )}
        </span>
        <span className="inline-flex shrink-0 items-center gap-1.5 type-subtext text-muted-foreground transition-colors group-hover:text-foreground">
          See runs
          <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
        </span>
      </button>
    </CardStrip>
  );
}

/** The gate is an ATTRIBUTE the gated steps share, not a stage: this note states
 *  the single rule behind the Gated mark that Ship and Verify both carry. */
function GateReasonNote() {
  return (
    <div className="flex gap-2.5 rounded-md bg-muted px-3 py-2.5">
      <ShieldCheck className="mt-px size-4 shrink-0 text-primary" />
      <p className="type-subtext text-muted-foreground">
        Ship and Verify are gated by the{" "}
        <span className="text-foreground">same rule — separation of duties</span>: the agent that
        verifies must be different from the one that shipped, so the same identity can never ship and
        approve the same work.
      </p>
    </div>
  );
}

/** A plain stage card (title · badge · action + body) — the boundary stages
 *  (Done) that aren't clickable sub-workflows. */
function StageCard({
  title,
  badge,
  action,
  children,
}: {
  title: string;
  badge?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card className="flex flex-col gap-3 px-6 py-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
          <h3 className="type-title font-medium text-foreground">{title}</h3>
          {badge}
        </div>
        {action}
      </div>
      {children}
    </Card>
  );
}

/** One live Definition-of-Done criterion, shaped like `dod_evaluate` output
 *  (RECURSIVE-DONE-PRD.md §13). `binding:false` is a proposal that surfaces but
 *  does not gate. */
type DodFactView = {
  label: string;
  pass: boolean;
  proof?: string;
  source?: string;
  binding?: boolean;
};

/** Done, given a real definition — the workflow's end STATE, not a hand-off.
 *  There is NO demo fallback: the "Done when" panel renders only when live DoD
 *  criteria are threaded in (the workflow-level DoD binding is the Done-tab
 *  track's scope) — a live page never shows invented criteria. */
function DoneCard({ dod }: { dod?: { criteria: DodFactView[]; subtreeDone?: boolean } } = {}) {
  const binding = dod?.criteria.filter((c) => c.binding !== false) ?? [];
  const proposals = dod?.criteria.filter((c) => c.binding === false) ?? [];

  return (
    <StageCard title="Done" badge={<Chip>end state</Chip>}>
      <p className="type-body text-muted-foreground">
        A run is <span className="text-foreground">Done</span> only once the verifier has approved and
        the work is merged to <code>main</code>. Because a different agent must sign off,
        &ldquo;Done&rdquo; here always means{" "}
        <span className="text-foreground">verified &amp; shipped</span> — never self-approved.
      </p>
      <CardStrip
        frame="px6py4"
        className="flex flex-wrap gap-x-4 gap-y-1.5 type-subtext text-muted-foreground"
      >
        <DoneFact>a different agent approved</DoneFact>
        <DoneFact>commit merged to main</DoneFact>
        <DoneFact>task closed · activity logged</DoneFact>
        {dod?.subtreeDone !== undefined && (
          <DoneFact
            pass={dod.subtreeDone}
            proof="the tree clause: a parent can't be done while a child is open"
          >
            every subtask Done
          </DoneFact>
        )}
        {binding.map((c) => (
          <DoneFact key={c.label} pass={c.pass} proof={c.proof}>
            {c.label}
          </DoneFact>
        ))}
      </CardStrip>

      {dod && dod.criteria.length > 0 && (
        <div className="mt-3 space-y-1.5">
          <p className="type-subtext font-medium text-muted-foreground">Done when</p>
          {dod.criteria.map((c) => (
            <div key={`when-${c.label}`} className="flex items-start gap-2 type-subtext">
              {c.pass ? (
                <Check className="mt-0.5 size-3.5 shrink-0 text-success" aria-label="met" />
              ) : (
                <X className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" aria-label="unmet" />
              )}
              <span className="flex-1">
                <span className={c.pass ? "text-foreground" : "text-muted-foreground"}>
                  {c.label}
                </span>
                {c.source && <Chip>{c.source}</Chip>}
                {c.binding === false && (
                  <span className="ml-1 text-muted-foreground">· proposal</span>
                )}
                {c.proof && <span className="block text-muted-foreground">{c.proof}</span>}
              </span>
            </div>
          ))}
          {proposals.length > 0 && (
            <p className="pt-0.5 type-subtext text-muted-foreground">
              {proposals.length} proposed criterion{proposals.length === 1 ? "" : "a"} shown but not
              yet gating — a derived/learned bar binds only after a light confirm.
            </p>
          )}
        </div>
      )}
    </StageCard>
  );
}

function DoneFact({
  children,
  pass = true,
  proof,
}: {
  children: React.ReactNode;
  pass?: boolean;
  proof?: string;
}) {
  return (
    <span className="inline-flex items-center gap-1.5" title={proof}>
      {pass ? (
        <Check className="size-3.5 text-success" />
      ) : (
        <X className="size-3.5 text-muted-foreground" />
      )}
      {children}
    </span>
  );
}

// ══ RUNS (port of `_runs-live.tsx`) ══════════════════════════════════════════

/**
 * The Activity chart above, the runs table (or a step's failure drill-in) below.
 *
 * All three surfaces are `@excellent/ui` blocks reading `workflowRuns.*`, which
 * were ALREADY mounted on the Hono host (`server/routes/projects.ts`) — so this
 * tab needed no new endpoint at all. The only change from the original is that
 * `from`/`to` arrive as typed search props instead of `useSearchParams()` reads.
 */
function RunsTab({
  loopId,
  rangeDays,
  failedStep,
  selectedFrom,
  selectedTo,
  onClearFilter,
  onSelectDay,
}: {
  loopId: number;
  /** Trailing window from the page's report range; null = all time. */
  rangeDays: number | null;
  /** Set when a step's failure strip sent you here. */
  failedStep: WorkflowStepDto | null;
  selectedFrom: string | null;
  selectedTo: string | null;
  onClearFilter: () => void;
  onSelectDay: (day: string) => void;
}) {
  // A single selected day is a one-day range; a wider hand-set range marks nothing.
  const selectedDay = selectedFrom && selectedFrom === selectedTo ? selectedFrom : null;

  return (
    <div className="flex flex-col gap-6">
      <RunsActivityCard
        loopId={loopId}
        days={rangeDays}
        selectedDay={selectedDay}
        onSelectDay={onSelectDay}
      />
      {/* A filter arriving from a step card WINS over a day selection: you clicked
          a specific failure, and answering with a different question would be the
          page losing your place. A step with no role can't be scoped (the ledger
          attributes runs by role), so it falls back to the full table rather than
          showing an empty drill-in. */}
      {failedStep?.role ? (
        <WorkflowFailedRunsCard
          loopId={loopId}
          role={failedStep.role}
          stepName={failedStep.name}
          onClear={onClearFilter}
        />
      ) : (
        <WorkflowRunsTable loopId={loopId} />
      )}
    </div>
  );
}
